import { DEG, clamp01 } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { HatchingValues } from './params';

/**
 * Hatching: fills the glyphs (or the space around them) with ruled lines.
 *
 * The lines are generated by marching along a rotated axis and sampling the distance
 * field at a fixed step, emitting a stroke wherever the samples are inside the region.
 * Sampling the *field* rather than clipping to a path is what allows the overshoot and
 * the depth-dependent density: both need to know how far inside the glyph a point is,
 * which a clipping mask cannot tell you.
 */
export interface HatchSegment {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  depth: number;
  layer: number;
}

export interface HatchingState {
  segments: HatchSegment[];
  palette: ResolvedPalette;
  densityCurve: Float32Array;
  layersDone: number;
  built: boolean;
}

function values(ctx: TechniqueContext): HatchingValues {
  return ctx.values as unknown as HatchingValues;
}

export function init(ctx: TechniqueContext): HatchingState {
  const v = values(ctx);
  return {
    segments: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    densityCurve: bakeCurve(v.densityCurve as Curve),
    layersDone: 0,
    built: false,
  };
}

export function step(state: HatchingState, _dt: number, ctx: TechniqueContext): HatchingState {
  if (state.built) return state;
  const v = values(ctx);

  const layer = state.layersDone;
  const angle = (v.angle + layer * v.layerAngle) * DEG;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const { width, height } = ctx;
  const diagonal = Math.hypot(width, height);
  const cx = width / 2;
  const cy = height / 2;
  const spacing = Math.max(0.5, v.spacing);
  const stride = Math.max(0.75, Math.min(3, spacing * 0.35));
  const inside = v.region === 'inside';

  // Walk parallel lines across the rotated bounding disc so every angle is covered.
  for (let offset = -diagonal / 2; offset <= diagonal / 2; offset += spacing) {
    let runStart: { x: number; y: number; depth: number } | null = null;
    let lastInside = { x: 0, y: 0 };

    for (let t = -diagonal / 2; t <= diagonal / 2; t += stride) {
      // Line direction (cos, sin), offset along the perpendicular (-sin, cos).
      let x = cx + cos * t - sin * offset;
      let y = cy + sin * t + cos * offset;

      if (v.waveAmplitude > 0) {
        const wave = Math.sin((t / Math.max(1, spacing)) * v.waveFrequency) * v.waveAmplitude;
        x += -sin * wave;
        y += cos * wave;
      }

      const signed = ctx.field.distance(x, y);
      const depth = inside ? -signed : signed;
      // The overshoot lets a stroke continue past the contour by a fixed amount.
      const isIn = depth > -v.overshoot;

      if (isIn) {
        // Depth-dependent density: shallow areas keep only part of their strokes.
        const norm = clamp01(depth / Math.max(1, v.depthReach));
        const keep = clamp01(sampleBaked(state.densityCurve, norm));
        if (keep > 0.02) {
          if (!runStart) runStart = { x, y, depth: Math.max(0, depth) };
          lastInside = { x, y };
        } else if (runStart) {
          state.segments.push({ x0: runStart.x, y0: runStart.y, x1: lastInside.x, y1: lastInside.y, depth: runStart.depth, layer });
          runStart = null;
        }
      } else if (runStart) {
        state.segments.push({ x0: runStart.x, y0: runStart.y, x1: lastInside.x, y1: lastInside.y, depth: runStart.depth, layer });
        runStart = null;
      }
    }
    if (runStart) {
      state.segments.push({ x0: runStart.x, y0: runStart.y, x1: lastInside.x, y1: lastInside.y, depth: runStart.depth, layer });
    }
  }

  state.layersDone++;
  if (state.layersDone >= v.layers) state.built = true;
  return state;
}

export function isComplete(state: HatchingState): boolean {
  return state.built;
}

export function draw(state: HatchingState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineWidth = v.lineWidth;
  c.lineCap = 'round';

  // Group by colour so the whole hatch is a handful of stroked paths rather than one
  // path per line.
  const buckets = new Map<string, Path2D>();
  for (const segment of state.segments) {
    if (Math.hypot(segment.x1 - segment.x0, segment.y1 - segment.y0) < 0.5) continue;

    let t: number;
    if (v.colorMode === 'layer') t = v.layers > 1 ? segment.layer / (v.layers - 1) : 0;
    else if (v.colorMode === 'depth') t = clamp01(segment.depth / Math.max(1, v.depthReach));
    else t = 0;

    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    let path = buckets.get(color);
    if (!path) {
      path = new Path2D();
      buckets.set(color, path);
    }
    path.moveTo(segment.x0, segment.y0);
    path.lineTo(segment.x1, segment.y1);
  }

  for (const [color, path] of buckets) {
    c.strokeStyle = color;
    c.stroke(path);
  }
}

export function elementCount(state: HatchingState): number {
  return state.segments.length;
}

export function toSVG(state: HatchingState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const groups = new Map<string, string[]>();

  for (const segment of state.segments) {
    if (Math.hypot(segment.x1 - segment.x0, segment.y1 - segment.y0) < 0.5) continue;
    let t: number;
    if (v.colorMode === 'layer') t = v.layers > 1 ? segment.layer / (v.layers - 1) : 0;
    else if (v.colorMode === 'depth') t = clamp01(segment.depth / Math.max(1, v.depthReach));
    else t = 0;
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(t);
    const list = groups.get(color) ?? [];
    list.push(`M${f(segment.x0)} ${f(segment.y0)}L${f(segment.x1)} ${f(segment.y1)}`);
    groups.set(color, list);
  }

  const parts: string[] = [`<g fill="none" stroke-width="${v.lineWidth}" stroke-linecap="round" stroke-opacity="${v.opacity}">`];
  for (const [color, paths] of groups) parts.push(`<path stroke="${color}" d="${paths.join('')}"/>`);
  parts.push('</g>');
  return parts.join('\n');
}
